import React, { useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  HStack,
  VStack,
  Text,
  Badge,
  Link,
  Divider,
  Box,
  Grid,
  Flex,
  Image,
} from '@chakra-ui/react';
import { useCountdown } from '../../hooks/useCountdown';
import { useSettings } from '@/hooks/useSettings';
import { assetUrl, sanitizeClubName } from '../../utils/url';
import { TeamLogo } from '../common/TeamLogo';
import MatchWeather from '../common/MatchWeather';
import { format, parse } from 'date-fns';
import { cs } from 'date-fns/locale';

const parseScore = (score?: string | null): { h: number; a: number } | null => {
  if (!score) return null;
  const m = score.match(/^(\d+)\s*[:\-]\s*(\d+)$/);
  if (!m) return null;
  return { h: parseInt(m[1], 10), a: parseInt(m[2], 10) };
};

export type FacrMatchLike = {
  id?: string | number;
  date?: string; // yyyy-mm-dd
  time?: string; // HH:MM
  date_time?: string; // alternative combined format (dd.MM.yyyy HH:mm)
  home?: string;
  away?: string;
  home_logo_url?: string;
  away_logo_url?: string;
  home_id?: string;
  away_id?: string;
  competition?: string;
  competitionName?: string;
  venue?: string;
  score?: string | null;
  facr_link?: string | null;
  report_url?: string | null;
};

interface MatchModalProps {
  isOpen: boolean;
  match: FacrMatchLike | null;
  onClose: () => void;
  onTeamClick?: (teamName: string, teamLogoUrl?: string) => void;
}

const formatWhen = (m: FacrMatchLike | null) => {
  if (!m) return '';
  try {
    if (m.date && m.time) {
      const d = new Date(`${m.date}T${(m.time || '00:00')}:00`);
      if (!isNaN(d.getTime())) return d.toLocaleString();
    }
    if (m.date_time) {
      // Try to parse dd.MM.yyyy HH:mm quickly by reordering
      const dt = String(m.date_time);
      const [dPart, tPart] = dt.split(' ');
      const [dd, MM, yyyy] = (dPart || '').split('.');
      if (dd && MM && yyyy) {
        const iso = `${yyyy}-${MM.padStart(2, '0')}-${dd.padStart(2, '0')}T${(tPart || '00:00')}:00`;
        const d = new Date(iso);
        if (!isNaN(d.getTime())) return d.toLocaleString();
      }
      return m.date_time;
    }
  } catch {}
  return '';
};

export const MatchModal: React.FC<MatchModalProps> = ({ isOpen, match, onClose, onTeamClick }) => {
  const { settings } = useSettings();
  
  const kickoffIso = useMemo(() => {
    if (!match) return null;
    if (match.date && match.time) return `${match.date}T${(match.time || '00:00')}:00`;
    if (match.date_time) {
      const dt = String(match.date_time);
      const [dPart, tPart] = dt.split(' ');
      const [dd, MM, yyyy] = (dPart || '').split('.');
      if (dd && MM && yyyy) return `${yyyy}-${MM.padStart(2, '0')}-${dd.padStart(2, '0')}T${(tPart || '00:00')}:00`;
      return match.date_time; // fallback
    }
    return null;
  }, [match]);

  const { countdownString, isActive, timeRemaining } = useCountdown(kickoffIso, 1000);
  const facrLink = match?.facr_link || match?.report_url || null;
  const when = formatWhen(match);

  // Determine if match has started (countdown finished) but no score yet
  const matchStarted = kickoffIso ? new Date(kickoffIso).getTime() <= Date.now() : false;
  const hasScore = match?.score && match.score.trim() !== '';

  // Check if this is a home match
  const isHomeMatch = useMemo(() => {
    if (!match?.home || !settings) return false;
    const homeTeam = sanitizeClubName(match.home).toLowerCase();
    const clubName = sanitizeClubName((settings as any)?.club_name || '').toLowerCase();
    return homeTeam.includes(clubName) || clubName.includes(homeTeam);
  }, [match, settings]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {sanitizeClubName(match?.home) || 'Domácí'} vs {sanitizeClubName(match?.away) || 'Hosté'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {match && (
            <VStack align="stretch" spacing={4}>
              {/* Show match status (Výhra/Remíza/Prohra) if determinable; otherwise fall back to competition name */}
              {(() => {
                const isPast = kickoffIso ? new Date(kickoffIso).getTime() <= Date.now() : false;
                const hasScore = match?.score && match.score.trim() !== '';
                
                if (!isPast) {
                  // Future match - show competition name
                  const compName = match.competition || match.competitionName;
                  if (compName && compName !== 'Všechny soutěže') {
                    return (
                      <Flex justify="center">
                        <Badge colorScheme="purple">{compName}</Badge>
                      </Flex>
                    );
                  }
                } else if (hasScore) {
                  // Past match with score - show sentiment
                  const s = parseScore(match.score);
                  if (s) {
                    const ourIsHome = settings && match.home && (
                      sanitizeClubName(match.home).toLowerCase().includes(sanitizeClubName((settings as any)?.club_name || '').toLowerCase()) ||
                      sanitizeClubName((settings as any)?.club_name || '').toLowerCase().includes(sanitizeClubName(match.home).toLowerCase())
                    );
                    const ourIsAway = settings && match.away && (
                      sanitizeClubName(match.away).toLowerCase().includes(sanitizeClubName((settings as any)?.club_name || '').toLowerCase()) ||
                      sanitizeClubName((settings as any)?.club_name || '').toLowerCase().includes(sanitizeClubName(match.away).toLowerCase())
                    );
                    
                    if (ourIsHome || ourIsAway) {
                      const ourGoals = ourIsHome ? s.h : s.a;
                      const oppGoals = ourIsHome ? s.a : s.h;
                      let label = 'Remíza';
                      let color = 'blue';
                      if (ourGoals > oppGoals) { label = 'Výhra'; color = 'green'; }
                      else if (ourGoals < oppGoals) { label = 'Prohra'; color = 'red'; }
                      
                      return (
                        <Flex justify="center">
                          <Badge colorScheme={color as any} variant="subtle">{label}</Badge>
                        </Flex>
                      );
                    }
                  }
                }
                return null;
              })()}
              <Flex align="center" justify="center" gap={3}>
                {match.home_logo_url && (
                  <Image 
                    src={match.home_logo_url} 
                    alt={match.home} 
                    boxSize="40px" 
                    borderRadius="full"
                    cursor="pointer"
                    onClick={() => onTeamClick && onTeamClick(match.home || '', match.home_logo_url)}
                    _hover={{ opacity: 0.8, transform: 'scale(1.1)' }}
                    transition="all 0.2s"
                    title={`Klikněte pro zobrazení statistik: ${match.home}`}
                  />
                )}
                {(() => {
                  const isPast = kickoffIso ? new Date(kickoffIso).getTime() <= Date.now() : false;
                  const hasScore = Boolean(match.score);
                  
                  // For future matches, always show countdown or "vs" - never score
                  if (!isPast) {
                    if (isActive && countdownString) {
                      return (
                        <Badge colorScheme="orange" fontSize="md" px={3} py={1}>za {countdownString}</Badge>
                      );
                    }
                    return (
                      <Badge colorScheme="gray" fontSize="md" px={3} py={1}>vs</Badge>
                    );
                  }
                  
                  // For past matches, show score or "vs"
                  return (
                    <Badge colorScheme={hasScore ? 'gray' : 'gray'} fontSize="md" px={3} py={1}>
                      {hasScore ? match.score : 'vs'}
                    </Badge>
                  );
                })()}
                {match.away_logo_url && (
                  <Image 
                    src={match.away_logo_url} 
                    alt={match.away} 
                    boxSize="40px" 
                    borderRadius="full"
                    cursor="pointer"
                    onClick={() => onTeamClick && onTeamClick(match.away || '', match.away_logo_url)}
                    _hover={{ opacity: 0.8, transform: 'scale(1.1)' }}
                    transition="all 0.2s"
                    title={`Klikněte pro zobrazení statistik: ${match.away}`}
                  />
                )}
              </Flex>

              <Divider />

              {/* Date and Time Display with Countdown */}
              <Box textAlign="center">
                <Text fontSize="lg" fontWeight="semibold" color="gray.800" mb={1}>
                  {(() => {
                    try {
                      // Try multiple date formats and use Czech locale
                      if (match.date) {
                        let dateObj: Date;
                        // Try yyyy-MM-dd format first
                        const parsed1 = parse(match.date, 'yyyy-MM-dd', new Date());
                        if (!isNaN(parsed1.getTime())) {
                          dateObj = parsed1;
                        } else {
                          // Try dd.MM.yyyy format
                          const parsed2 = parse(match.date, 'dd.MM.yyyy', new Date());
                          if (!isNaN(parsed2.getTime())) {
                            dateObj = parsed2;
                          } else {
                            return match.date;
                          }
                        }
                        return format(dateObj, 'EEEE d. MMMM yyyy', { locale: cs });
                      }
                      return '';
                    } catch (error) {
                      console.error('Date formatting error:', error);
                      return match.date || '';
                    }
                  })()}
                </Text>
                <Text fontSize="md" color="gray.700">
                  {match.time || '—'}
                </Text>
              </Box>

              {/* Enhanced Countdown Display for Upcoming Matches */}
              {(() => {
                const isPast = kickoffIso ? new Date(kickoffIso).getTime() <= Date.now() : false;
                const hasScore = Boolean(match.score);
                
                if (!hasScore && !isPast && isActive && timeRemaining && timeRemaining > 0) {
                  const days = Math.floor(timeRemaining / (24 * 60 * 60 * 1000));
                  const hours = Math.floor((timeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
                  const minutes = Math.floor((timeRemaining % (60 * 60 * 1000)) / (60 * 1000));
                  const seconds = Math.floor((timeRemaining % (60 * 1000)) / 1000);
                  
                  return (
                    <Box 
                      mt={4} 
                      p={4} 
                      bg="orange.50" 
                      borderRadius="lg" 
                      borderWidth="2px" 
                      borderColor="orange.200"
                    >
                      <Text fontSize="sm" fontWeight="semibold" color="orange.800" mb={3} textAlign="center">
                        Zápas začíná za
                      </Text>
                      <Grid 
                        templateColumns={days > 0 ? "repeat(4, 1fr)" : "repeat(3, 1fr)"} 
                        gap={3}
                      >
                        {days > 0 && (
                          <Box textAlign="center">
                            <Box 
                              bg="white" 
                              borderRadius="md" 
                              p={3} 
                              borderWidth="1px" 
                              borderColor="orange.300"
                              boxShadow="sm"
                            >
                              <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                                {days}
                              </Text>
                            </Box>
                            <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                              {days === 1 ? 'den' : days < 5 ? 'dny' : 'dní'}
                            </Text>
                          </Box>
                        )}
                        <Box textAlign="center">
                          <Box 
                            bg="white" 
                            borderRadius="md" 
                            p={3} 
                            borderWidth="1px" 
                            borderColor="orange.300"
                            boxShadow="sm"
                          >
                            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                              {String(hours).padStart(2, '0')}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                            {hours === 1 ? 'hodina' : hours < 5 ? 'hodiny' : 'hodin'}
                          </Text>
                        </Box>
                        <Box textAlign="center">
                          <Box 
                            bg="white" 
                            borderRadius="md" 
                            p={3} 
                            borderWidth="1px" 
                            borderColor="orange.300"
                            boxShadow="sm"
                          >
                            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                              {String(minutes).padStart(2, '0')}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                            {minutes === 1 ? 'minuta' : minutes < 5 ? 'minuty' : 'minut'}
                          </Text>
                        </Box>
                        <Box textAlign="center">
                          <Box 
                            bg="white" 
                            borderRadius="md" 
                            p={3} 
                            borderWidth="1px" 
                            borderColor="orange.300"
                            boxShadow="sm"
                          >
                            <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                              {String(seconds).padStart(2, '0')}
                            </Text>
                          </Box>
                          <Text fontSize="xs" color="gray.600" mt={1} fontWeight="medium">
                            {seconds === 1 ? 'sekunda' : seconds < 5 ? 'sekundy' : 'sekund'}
                          </Text>
                        </Box>
                      </Grid>
                    </Box>
                  );
                }
                return null;
              })()}
              
              <Box h="1px" bg="gray.200" />
              
              {/* Venue information */}
              {match.venue && (
                <Text fontSize="md" color="gray.700" textAlign="center">
                  <strong>Kde:</strong> {match.venue}
                </Text>
              )}
              
              {/* Weather widget for home matches that haven't started */}
              {(() => {
                const isPast = kickoffIso ? new Date(kickoffIso).getTime() <= Date.now() : false;
                const hasScore = Boolean(match.score);
                
                if (isHomeMatch && !isPast && !hasScore) {
                  return (
                    <Box>
                      <MatchWeather
                        matchDateTime={kickoffIso || ''}
                        venue={match.venue}
                        isHomeMatch={true}
                        matchHasStarted={false}
                      />
                    </Box>
                  );
                }
                return null;
              })()}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {facrLink && (
            <Button 
              colorScheme="blue" 
              mr={3}
              onClick={(e) => {
                e.preventDefault();
                // Open in background tab without switching focus
                const link = document.createElement('a');
                link.href = facrLink;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
            >
              Detail na FAČR
            </Button>
          )}
          <Button onClick={onClose}>Zavřít</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default MatchModal;
